import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  globalFileServer,
  newGlobalFileServer,
} from "../../../../axiosInstance";
import { checkout } from "../../../server/Order";
import showAlert from "../../../server/ShowAlert";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearCart, upsertCartItem } from "../../../server/CollectSite";
import PrivateArea from "../../privateArea/PrivateArea";
import PayPopup from "../shopCart/PayPopup";
import { getStationToCustomer } from "../../../server/Station";
import axios from "axios";
import { awsUrl } from "../../../../axiosInstance";
import { SaveNedarimData } from "../../../server/Payment";
import { UpdateUser } from "../../../server/Customer";
import CouponCode from "./CouponCode";
import html2pdf from "html2pdf.js";
import { OrderReceiptTemplate } from "./OrderReceiptTemplate";
import ProductAddToCart from "../productPage/ProductAddToCart";
import {
  X,
  Pencil,
  Trash2,
  Check,
  User,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

/* ─── Step config ─────────────────────────────────────────── */
const CHECKOUT_STEPS = ["פרטי לקוח", "משלוח", "תשלום", "תודה רבה"];

/* ─── Re-use CartDrawer's image helper ───────────────────── */
function cartLineImageSrc(product) {
  const mediaArray = Array.isArray(product?.media) ? product.media : [];
  const path =
    mediaArray.length > 0 ? mediaArray[mediaArray.length - 1]?.path : null;
  return path
    ? `${newGlobalFileServer}${path}?width=80&height=80`
    : `${newGlobalFileServer}placeholder.jpg`;
}

/* ─── Re-use CartDrawer's meta line ──────────────────────── */
function CartLineMeta({ product }) {
  if (!product) return null;
  const kashrut = product.kosherSupervision?.[0]?.classification ?? "";
  return (
    <div className="prod-info-line">
      {product.subBrand?.name ? (
        <>
          <p className="prod-brand">{product.subBrand.name}</p>
          <span className="line">|</span>
        </>
      ) : null}
      {product.weightDetails ? (
        <>
          {product.logisticsDetails?.packageQuantity > 1 ? (
            <span className="prod-details">
              {product.logisticsDetails.packageQuantity} ×
            </span>
          ) : null}
          <span className="prod-details">
            {product.weightDetails?.netContent}
          </span>
          <span className="prod-details">
            {product.weightDetails.unitOfMeasure}
          </span>
        </>
      ) : null}
      {(kashrut === "בשרי" || kashrut === "חלבי" || kashrut === "פרווה") && (
        <>
          {(product.weightDetails || product.subBrand?.name) && (
            <span className="line">|</span>
          )}
          <span
            className={
              kashrut === "בשרי"
                ? "fleshy"
                : kashrut === "חלבי"
                  ? "milky"
                  : "parve"
            }
          >
            {kashrut}
          </span>
        </>
      )}
    </div>
  );
}

/* ─── Checkmark SVG ──────────────────────────────────────── */
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    width="13"
    height="13"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Deferred delivery fee notice ─────────────────────── */
const DeferredDeliveryNotice = ({ fee }) => {
  if (!fee || fee <= 0) return null;
  return (
    <div className="co-delivery-notice" role="note">
      דמי משלוח בסך <strong>₪{Number(fee).toFixed(2)}</strong> אינם נגבים כעת —
      התשלום יבוצע ישירות לשליח בעת קבלת המשלוח.
    </div>
  );
};

/* ─── Lock SVG ───────────────────────────────────────────── */
const LockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ─── Payment status overlay (loading / success / error) ─── */
const PaymentStatusScreen = ({ status, onRetry, onHome }) => {
  useEffect(() => {
    if (status === "success") {
      const t = setTimeout(onHome, 3000);
      return () => clearTimeout(t);
    }
  }, [status, onHome]);

  const content = (
    <div
      className="pss-overlay"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      <div className="pss-card">
        {status === "loading" && (
          <>
            <div className="pss-loading-row">
              <div className="pss-spinner" aria-hidden />
              <p className="pss-title">מעבד את התשלום...</p>
            </div>
            <p className="pss-sub">אנא המתן, אל תסגור את הדף</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="pss-icon pss-icon--success">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                width="40"
                height="40"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11.5 14.5 15 10" strokeWidth="3" />
              </svg>
            </div>
            <p className="pss-title">ההזמנה התקבלה בהצלחה!</p>
            <p className="pss-sub">מעביר אותך לדף הבית...</p>
            <div className="pss-progress-bar">
              <div className="pss-progress-fill" />
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <div className="pss-icon pss-icon--error">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                width="40"
                height="40"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <p className="pss-title">התשלום נכשל</p>
            <p className="pss-sub">אנא בדוק את פרטי הכרטיס ונסה שנית</p>
            <button className="pss-retry" onClick={onRetry}>
              נסה שוב
            </button>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

/* ════════════════════════════════════════════════════════════
   ShopCart
════════════════════════════════════════════════════════════ */
const ShopCart = () => {
  const dispatch = useDispatch();
  const {
    user,
    _orderTime,
    productsInCart,
    deliveryPrice,
    orderSum,
    totalSum,
    stationId,
    deliveryAvailable,
  } = useSelector((state) => ({
    user: state.user.user,
    stationId: state.user.stationId,
    orderTime: state.productsInCart.orderTime,
    productsInCart: state.productsInCart.productsInCart,
    orderSum: state.productsInCart.orderSum,
    totalSum: state.productsInCart.totalSum,
    deliveryPrice: state.productsInCart.deliveryPrice,
    deliveryAvailable: state.productsInCart.deliveryAvailable,
  }));

  const navigate = useNavigate();
  const [allSum, setAllSum] = useState(0);
  const [toPayPopup, setToPayPopup] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // null = idle, "loading" = processing, "success" = done, "error" = failed
  const [station, setStation] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(0);
  const [displayShipping, setDisplayShipping] = useState(0);
  const [orderCompleteSummary, setOrderCompleteSummary] = useState(null);
  const [verified, setVerified] = useState(false);
  const [verifiedGetSms, setVerifiedGetSms] = useState(
    user.VerifiedGetSms || false,
  );
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState(null);
  const [comment, setComment] = useState("");
  const isCheckoutBlockedRef = useRef(false);
  const [isCheckoutInProgress, setIsCheckoutInProgress] = useState(false);

  /* ── NEW: single UI-only step state ── */
  const [currentStep, setCurrentStep] = useState(0);

  /* ── Editable user details (local, for this checkout session) ── */
  const [localFirstName, setLocalFirstName] = useState(user.firstName || "");
  const [localLastName, setLocalLastName] = useState(user.lastName || "");
  const [localPhone, setLocalPhone] = useState(user.phone || user.Phone || "");
  const [localEmail, setLocalEmail] = useState(user.email || "");
  const [editingDetails, setEditingDetails] = useState(false);

  /* ── Editable shipping address (local) — coerce to string ── */
  const [localStreet, setLocalStreet] = useState(String(user.addrStreet || ""));
  const [localHouse, setLocalHouse] = useState(String(user.addrHouse || ""));
  const [localFloor, setLocalFloor] = useState(String(user.addrFloor || ""));
  const [localApt, setLocalApt] = useState(String(user.addrApartment || ""));
  const [localCity, setLocalCity] = useState(
    String(user.addrCity || user.City || ""),
  );
  const [editingAddress, setEditingAddress] = useState(
    !(user.addrStreet || user.addrCity),
  );
  const [confirmDeleteAddr, setConfirmDeleteAddr] = useState(false);

  const getStation = useCallback(() => {
    getStationToCustomer(stationId).then((x) => setStation(x.data));
  }, [stationId]);

  useEffect(() => {
    const productsTotal = parseFloat(orderSum) + parseFloat(totalSum);
    const minFreeDelivery = station?.MinFreeDeleviry;
    const shouldChargeShipping =
      minFreeDelivery == null || productsTotal < Number(minFreeDelivery);
    const shipping = shouldChargeShipping ? Number(deliveryPrice || 0) : 0;
    setDisplayShipping(shipping);
    setAllSum(productsTotal);
    const finalTotal = productsTotal - Number(couponDiscount || 0);
    setPaymentOrder(Math.max(0, finalTotal));
  }, [totalSum, station, orderSum, deliveryPrice, couponDiscount]);

  useEffect(() => {
    getStation();
  }, [getStation]);

  /* ── Hide header "לתשלום" button while on checkout page ── */
  useEffect(() => {
    document.body.classList.add("in-checkout");
    return () => document.body.classList.remove("in-checkout");
  }, []);

  const handleCouponApplied = (discount, couponCode) => {
    setCouponDiscount(discount);
    setAppliedCouponCode(couponCode);
  };

  const releaseCheckoutLock = () => {
    isCheckoutBlockedRef.current = false;
    setIsCheckoutInProgress(false);
  };

  /* ── Delete item from cart (mirrors CartDrawer.handleDelete) ── */
  const handleDelete = (element) => {
    dispatch(
      upsertCartItem(
        element.productId,
        parseFloat(element.unitPrice).toFixed(2),
        0,
        String(stationId),
        element.product?.pricing?.promotions?.length
          ? element.product.pricing.promotions[0].promoId
          : undefined,
        element.product,
      ),
    );
  };

  const splitPaymentsPay = (
    _succ,
    PaymentMataraTokenId,
    NumPayment,
    responseData,
  ) => {
    if (!_succ) {
      setToPayPopup(false);
      releaseCheckoutLock();
      return;
    }

    setPaymentStatus("loading");

    const fullAddress = [
      localStreet,
      localHouse,
      localFloor ? `קומה ${localFloor}` : null,
      localApt ? `דירה ${localApt}` : null,
    ]
      .filter(Boolean)
      .join(", ");
    const params = { ...responseData, address: fullAddress };

    checkout(params, stationId)
      .then(async (res) => {
        dispatch({ type: "CHECKOUT_SUCCESS", payload: res.data });
        const orderData = res.data;
        let base64Result = "";

        try {
          const element = document.createElement("div");
          element.style.width = "190mm";
          element.innerHTML = OrderReceiptTemplate(
            orderData,
            {
              ...user,
              firstName: localFirstName,
              lastName: localLastName,
              phone: localPhone,
            },
            fullAddress,
            paymentOrder,
            productsInCart,
            displayShipping,
          );
          const opt = {
            margin: [10, 5, 20, 5],
            filename: `order_${orderData.id}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { scale: 3, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          };
          const worker = html2pdf()
            .set(opt)
            .from(element)
            .toPdf()
            .get("pdf")
            .then(function (pdf) {
              const totalPages = pdf.internal.getNumberOfPages();
              for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(10);
                pdf.text(i + " / " + totalPages, 105, 285, { align: "center" });
              }
            });

          const pdfArrayBuffer = await worker.output("arraybuffer");
          const bytes = new Uint8Array(pdfArrayBuffer);
          let binary = "";
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          base64Result = btoa(binary);
        } catch (pdfErr) {
          console.error("PDF generation error:", pdfErr);
        }

        const mailPayload = {
          OrderWebId: orderData.id || orderData.orderWebId,
          name: `${localFirstName} ${localLastName}`.trim(),
          email: localEmail || user.email,
          address: fullAddress,
          customerId: user.id,
          amount: paymentOrder,
          updated: new Date().toLocaleDateString("he-IL"),
          Digit4: orderData?.payment?.rawPayment?.LastNum || "****",
          PdfBase64: base64Result,
        };

        setToPayPopup(false);

        await axios.post(`${awsUrl}/v1/orders`, mailPayload, {
          headers: { Authorization: `bearer ${localStorage.jwtToken}` },
        });

        SaveNedarimData(
          orderData,
          orderData.id || orderData.OrderWebId,
          PaymentMataraTokenId,
        );
        setOrderCompleteSummary({
          itemCount: productsInCart.filter((p) => p.quantity > 0).length,
          total: paymentOrder,
          deliveryFee: displayShipping,
        });
        setPaymentStatus(null);
        setCurrentStep(3);
        setToPayPopup(false);
        dispatch(clearCart());
        releaseCheckoutLock();
      })
      .catch((error) => {
        setPaymentStatus("error");
        setToPayPopup(false);
        releaseCheckoutLock();
        console.error("orderPost Error:", error);
      });
  };

  const toPay = () => {
    if (isCheckoutBlockedRef.current) return;

    if (!verified) {
      error("יש לאשר את התקנון והצהרת הגיל לפני ביצוע ההזמנה", "error");
      return;
    }

    if (!deliveryAvailable) {
      error("לא ניתן לבצע הזמנה במחיר זה", "error");
      return;
    }

    const products = productsInCart.filter((element) => element.quantity > 0);
    if (!products.length) {
      navigate("/");
    }

    const sumBeforeDiscount = parseFloat(totalSum) + parseFloat(orderSum);
    if (sumBeforeDiscount >= station?.MinPrice || station?.MinPrice == null) {
      isCheckoutBlockedRef.current = true;
      setIsCheckoutInProgress(true);
      setToPayPopup(true);
    } else {
      error("מינימום הזמנה: " + station?.MinPrice + " ש״ח", "error");
    }
  };

  const error = (textError, type) => {
    showAlert({ msgTitle: textError, msgType: type });
  };

  const handleChange = (nameChanged) => {
    if (nameChanged === "Verified") {
      setVerified(!verified);
    } else if (nameChanged === "VerifiedGetSms") {
      setVerifiedGetSms(!verifiedGetSms);
    }
  };

  /* ── Step navigation ── */
  const goNext = () => setCurrentStep((s) => Math.min(s + 1, 3));
  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  /* ── Derived values ── */
  const activeItems = productsInCart.filter((p) => p.quantity > 0);
  const grandTotal = paymentOrder;
  const userInitials = [localFirstName, localLastName]
    .filter(Boolean)
    .map((n) => String(n)[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  /* Build the exact payload shape the API expects — all values coerced to strings */
  const str = (v) => String(v ?? "");
  const buildUpdatePayload = ({
    firstName,
    lastName,
    phone,
    street,
    house,
    floor,
    apt,
    zip,
  }) => ({
    defaultLocation: str(user.defaultLocation || stationId),
    firstName: str(firstName),
    lastName: str(lastName),
    phone: str(phone),
    addrStreet: str(street),
    addrHouse: str(house),
    addrFloor: str(floor),
    addrApartment: str(apt),
    addrZipCode: str(zip),
  });

  /* Validate required fields before PUT — returns true if OK */
  const s = (v) => String(v ?? "").trim(); /* safe string coercion */
  const validateBeforeSave = () => {
    if (!s(localFirstName) || !s(localLastName) || !s(localPhone)) {
      showAlert({
        msgTitle: "יש למלא שם פרטי, שם משפחה וטלפון",
        msgType: "error",
      });
      setEditingDetails(true);
      return false;
    }
    if (!s(localStreet) || !s(localHouse)) {
      showAlert({
        msgTitle: "יש למלא רחוב ומספר בית לפני השמירה",
        msgType: "error",
      });
      setEditingAddress(true);
      return false;
    }
    return true;
  };

  const saveDetails = async () => {
    if (!validateBeforeSave()) return;
    setIsSavingDetails(true);
    try {
      const payload = buildUpdatePayload({
        firstName: localFirstName,
        lastName: localLastName,
        phone: localPhone,
        street: localStreet,
        house: localHouse,
        floor: localFloor,
        apt: localApt,
        zip: user.addrZipCode || "",
      });
      await UpdateUser(payload);
      dispatch({ type: "UPDATE_USER", payload: { ...payload } });
      showAlert({ msgTitle: "הפרטים עודכנו בהצלחה", msgType: "success" });
      setEditingDetails(false);
    } catch (err) {
      console.error("saveDetails error:", err);
      showAlert({ msgTitle: "שגיאה בעדכון הפרטים", msgType: "error" });
    } finally {
      setIsSavingDetails(false);
    }
  };

  const saveAddress = async () => {
    if (!validateBeforeSave()) return;
    setIsSavingAddress(true);
    try {
      const payload = buildUpdatePayload({
        firstName: localFirstName,
        lastName: localLastName,
        phone: localPhone,
        street: localStreet,
        house: localHouse,
        floor: localFloor,
        apt: localApt,
        zip: user.addrZipCode || "",
      });
      await UpdateUser(payload);
      dispatch({ type: "UPDATE_USER", payload: { ...payload } });
      showAlert({ msgTitle: "הכתובת עודכנה בהצלחה", msgType: "success" });
      setEditingAddress(false);
    } catch (err) {
      console.error("saveAddress error:", err);
      showAlert({ msgTitle: "שגיאה בעדכון הכתובת", msgType: "error" });
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleDeleteAddress = () => {
    setLocalStreet("");
    setLocalHouse("");
    setLocalFloor("");
    setLocalApt("");
    setLocalCity("");
    setConfirmDeleteAddr(false);
    setEditingAddress(true);
  };

  const localAddrHasData = !!(localStreet || localCity);

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div className="checkout-page" dir="rtl">
      {/* ── FIX 1: Stepper is FIRST child, full width, above all columns ── */}
      <div className="co-stepper" aria-label="שלבי ההזמנה">
        <div className="co-stepper__track">
          {CHECKOUT_STEPS.map((label, idx) => {
            const isDone = idx < currentStep;
            const isActive = idx === currentStep;
            return (
              <div
                key={idx}
                className={[
                  "cstep",
                  isActive ? "cstep--active" : "",
                  isDone ? "cstep--done" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {idx > 0 && (
                  <div
                    className={`cstep__line${currentStep >= idx ? " cstep__line--done" : ""}`}
                  />
                )}
                <div
                  className="cstep__circle"
                  aria-current={isActive ? "step" : undefined}
                >
                  {isDone ? <CheckIcon /> : <span>{idx + 1}</span>}
                </div>
                <span className="cstep__label">{label}</span>
              </div>
            );
          })}
        </div>
        {/* Mobile compact indicator */}
        <div className="co-stepper__mobile">
          <span className="cstep-mob__name">{CHECKOUT_STEPS[currentStep]}</span>
          <span className="cstep-mob__count">
            שלב {currentStep + 1} מתוך {CHECKOUT_STEPS.length}
          </span>
        </div>
      </div>

      {/* ── FIX 1: co-body holds the two columns ── */}
      <div className={`co-body${currentStep === 3 ? " co-body--success" : ""}`}>
        {/* ── RIGHT column: one step at a time ── */}
        <div className="co-main">
          {/* ═══ STEP 0: פרטי לקוח ═══ */}
          {currentStep === 0 && (
            <div className="co-step-pane">
              {/* User header card */}
              <div className="co-s1-user-card">
                <div className="co-s1-avatar">{userInitials || "?"}</div>
                <div className="co-s1-user-info">
                  <span className="co-s1-name">
                    {localFirstName} {localLastName}
                  </span>
                  <span className="co-s1-email">{localEmail}</span>
                </div>
              </div>

              {/* Customer details — view / edit toggle */}
              <div className="co-card">
                <div className="co-card__head co-card__head--spread">
                  <div className="co-card__head-left">
                    <span className="co-card__title">פרטי לקוח</span>
                  </div>
                  {editingDetails ? (
                    <button
                      type="button"
                      className={`co-confirm-btn${isSavingDetails ? " co-confirm-btn--loading" : ""}`}
                      onClick={saveDetails}
                      disabled={isSavingDetails}
                      aria-label="שמור פרטים"
                    >
                      {isSavingDetails ? (
                        <span className="co-confirm-btn__spinner" aria-hidden />
                      ) : (
                        <Check size={12} strokeWidth={2.5} aria-hidden />
                      )}
                      {isSavingDetails ? "שומר..." : "שמור"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="co-icon-btn"
                      onClick={() => setEditingDetails(true)}
                      aria-label="ערוך פרטי לקוח"
                      title="ערוך פרטים"
                    >
                      <Pencil size={13} strokeWidth={2} aria-hidden />
                    </button>
                  )}
                </div>

                <div className="co-card__body">
                  {editingDetails ? (
                    /* ── Edit mode ── */
                    <>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">שם פרטי</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localFirstName}
                            onChange={(e) => setLocalFirstName(e.target.value)}
                            autoComplete="given-name"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">שם משפחה</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localLastName}
                            onChange={(e) => setLocalLastName(e.target.value)}
                            autoComplete="family-name"
                          />
                        </div>
                      </div>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">טלפון</label>
                          <input
                            className="co-input"
                            type="tel"
                            value={localPhone}
                            onChange={(e) => setLocalPhone(e.target.value)}
                            autoComplete="tel"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">אימייל</label>
                          <input
                            className="co-input co-input--ltr"
                            type="email"
                            value={localEmail}
                            onChange={(e) => setLocalEmail(e.target.value)}
                            autoComplete="email"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    /* ── View mode ── */
                    <ul className="co-info-list">
                      {(localFirstName || localLastName) && (
                        <li className="co-info-item">
                          <User
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span>
                            {localFirstName} {localLastName}
                          </span>
                        </li>
                      )}
                      {localEmail && (
                        <li className="co-info-item">
                          <Mail
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span dir="ltr">{localEmail}</span>
                        </li>
                      )}
                      {localPhone && (
                        <li className="co-info-item">
                          <Phone
                            size={13}
                            className="co-info-icon"
                            aria-hidden
                          />
                          <span dir="ltr">{localPhone}</span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>

              <button type="button" className="co-s1-next" onClick={goNext}>
                המשך
              </button>
            </div>
          )}

          {/* ═══ STEP 1: משלוח ═══ */}
          {currentStep === 1 && (
            <div className="co-step-pane">
              {/* Shipping address — view / edit / delete */}
              <div className="co-card">
                <div className="co-card__head co-card__head--spread">
                  <div className="co-card__head-left">
                    <span className="co-card__title">כתובת למשלוח</span>
                  </div>
                  <div className="co-card__head-actions">
                    {editingAddress ? (
                      /* ── Editing: show "שמור" to persist ── */
                      <button
                        type="button"
                        className={`co-confirm-btn${isSavingAddress ? " co-confirm-btn--loading" : ""}`}
                        onClick={saveAddress}
                        disabled={isSavingAddress}
                        aria-label="שמור כתובת"
                      >
                        {isSavingAddress ? (
                          <span
                            className="co-confirm-btn__spinner"
                            aria-hidden
                          />
                        ) : (
                          <Check size={12} strokeWidth={2.5} aria-hidden />
                        )}
                        {isSavingAddress ? "שומר..." : "שמור"}
                      </button>
                    ) : (
                      /* ── View: pencil + trash ── */
                      <>
                        <button
                          type="button"
                          className="co-icon-btn"
                          onClick={() => setEditingAddress(true)}
                          aria-label="ערוך כתובת"
                          title="ערוך כתובת"
                        >
                          <Pencil size={13} strokeWidth={2} aria-hidden />
                        </button>
                        {!confirmDeleteAddr ? (
                          <button
                            type="button"
                            className="co-icon-btn co-icon-btn--danger"
                            onClick={() => setConfirmDeleteAddr(true)}
                            aria-label="מחק כתובת"
                            title="מחק כתובת"
                          >
                            <Trash2 size={13} strokeWidth={2} aria-hidden />
                          </button>
                        ) : (
                          <span className="co-delete-confirm">
                            <button
                              type="button"
                              className="co-delete-confirm__yes"
                              onClick={handleDeleteAddress}
                            >
                              מחק
                            </button>
                            <button
                              type="button"
                              className="co-delete-confirm__no"
                              onClick={() => setConfirmDeleteAddr(false)}
                            >
                              ביטול
                            </button>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="co-card__body">
                  {editingAddress ? (
                    /* ── Edit form ── */
                    <>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">רחוב</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localStreet}
                            onChange={(e) => setLocalStreet(e.target.value)}
                            placeholder="שם הרחוב"
                            autoComplete="address-line1"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">מספר בית</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localHouse}
                            onChange={(e) => setLocalHouse(e.target.value)}
                            placeholder="10"
                          />
                        </div>
                      </div>
                      <div className="co-field-row">
                        <div className="co-field">
                          <label className="co-label">קומה</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localFloor}
                            onChange={(e) => setLocalFloor(e.target.value)}
                            placeholder="1"
                          />
                        </div>
                        <div className="co-field">
                          <label className="co-label">דירה</label>
                          <input
                            className="co-input"
                            type="text"
                            value={localApt}
                            onChange={(e) => setLocalApt(e.target.value)}
                            placeholder="1"
                          />
                        </div>
                      </div>
                      <div className="co-field">
                        <label className="co-label">עיר</label>
                        <input
                          className="co-input"
                          type="text"
                          value={localCity}
                          onChange={(e) => setLocalCity(e.target.value)}
                          placeholder="תל אביב"
                          autoComplete="address-level2"
                        />
                      </div>
                    </>
                  ) : /* ── View mode ── */
                  localAddrHasData ? (
                    <div className="co-addr-chip co-addr-chip--selected">
                      <div className="co-addr-radio">
                        <div className="co-addr-radio__dot" />
                      </div>
                      <div className="co-addr-body">
                        <span className="co-addr-name">
                          {localFirstName} {localLastName}
                        </span>
                        <span className="co-addr-line">
                          {[
                            localStreet,
                            localHouse,
                            localFloor ? `קומה ${localFloor}` : null,
                            localApt ? `דירה ${localApt}` : null,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                        {localCity && (
                          <span className="co-addr-line">{localCity}</span>
                        )}
                        {localPhone && (
                          <span className="co-addr-line">{localPhone}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="co-addr-empty">
                      לא הוגדרה כתובת — לחץ על עריכה להוספה
                    </p>
                  )}
                </div>
              </div>

              <div className="co-card">
                <div className="co-card__head">
                  <span className="co-card__title">הערה וקוד קופון</span>
                </div>
                <div className="co-card__body">
                  <textarea
                    className="co-textarea"
                    placeholder="הערה להזמנה"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <p className="co-comment-note">
                    * אין כל התחייבות למלא אחר הבקשה
                  </p>
                  <CouponCode
                    userId={user.id}
                    orderSum={parseFloat(totalSum) + parseFloat(orderSum)}
                    onCouponApplied={handleCouponApplied}
                  />
                </div>
              </div>

              <div className="co-step-nav">
                <button
                  type="button"
                  className="co-btn co-btn--ghost"
                  onClick={goBack}
                >
                  חזור
                </button>
                <button
                  type="button"
                  className="co-btn co-btn--primary"
                  onClick={goNext}
                >
                  המשך
                </button>
              </div>
            </div>
          )}

          {/* ═══ STEP 2: תשלום ═══ */}
          {currentStep === 2 && (
            <div className="co-step-pane">
              {/* Price summary */}
              <div className="co-card">
                <div className="co-card__head">
                  <span className="co-card__title">סיכום ותשלום</span>
                </div>
                <div className="co-card__body">
                  <div className="order-summary">
                    <div className="order-summary__row">
                      <span>סיכום מוצרים</span>
                      <span>₪{allSum.toFixed(2)}</span>
                    </div>
                    {couponDiscount > 0 && (
                      <div className="order-summary__row discount">
                        <span>הנחת קופון</span>
                        <span>-₪{Number(couponDiscount).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="order-summary__row grand-total">
                      <span>סה&quot;כ לתשלום</span>
                      <span>₪{paymentOrder.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <DeferredDeliveryNotice fee={displayShipping} />

              {/* Terms checkboxes */}
              <div className="checkbox-style">
                <label>
                  <input
                    type="checkbox"
                    checked={verified}
                    onChange={() => handleChange("Verified")}
                  />
                  <span className="verify">
                    {" "}
                    הנני מאשר/ת כי קראתי את{" "}
                    <a
                      target="_blank"
                      href={globalFileServer + "policy_form.pdf"}
                      rel="noreferrer"
                    >
                      תקנון{" "}
                    </a>
                    אתר ׳המזון׳ ואת מדיניות הפרטיות, וכן הנני מאשר/ת כי אני מעל
                    גיל 18
                  </span>
                </label>
                <label className="verify">
                  <input
                    type="checkbox"
                    checked={verifiedGetSms}
                    onChange={() => handleChange("VerifiedGetSms")}
                  />
                  הנני מאשר/ת לקבל הטבות, ועדכונים בדוא״ל וב-SMS (ניתן להפסיק את
                  קבלת העדכונים בכל עת).
                </label>
              </div>

              {/* Navigation row — Back on right, Finish on left (RTL) */}
              <div className="co-step-nav">
                <button
                  type="button"
                  className="co-btn co-btn--ghost"
                  onClick={goBack}
                >
                  חזור
                </button>
                <button
                  onClick={toPay}
                  className={`co-btn co-btn--primary ${isCheckoutInProgress ? "disabled" : ""}`}
                  disabled={isCheckoutInProgress}
                >
                  סיום קנייה &gt;
                </button>
              </div>
            </div>
          )}

          {/* ═══ STEP 3: תודה רבה ═══ */}
          {currentStep === 3 && (
            <div className="co-step-pane co-success-pane">
              <div className="co-success-icon" aria-hidden>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="64"
                  height="64"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9 12 11.5 14.5 15 10" strokeWidth="2.5" />
                </svg>
              </div>
              <h2 className="co-success-heading">ההזמנה התקבלה בהצלחה!</h2>
              <p className="co-success-meta">
                {orderCompleteSummary?.itemCount ?? activeItems.length} פריטים ·
                ₪{(orderCompleteSummary?.total ?? grandTotal).toFixed(2)}
              </p>
              <DeferredDeliveryNotice
                fee={orderCompleteSummary?.deliveryFee ?? displayShipping}
              />
              <button
                type="button"
                className="co-btn co-btn--primary"
                onClick={() => navigate("/")}
              >
                חזרה לחנות
              </button>
            </div>
          )}
        </div>

        {/* ── LEFT column: order summary sidebar (hidden on success step) ── */}
        {currentStep !== 3 ? (
          <div className="co-sidebar">
            <div className="co-sidebar-card">
              {/* Sidebar header */}
              <div className="co-sidebar-head">
                <span className="co-sidebar-head__title">סיכום הזמנה</span>
                {activeItems.length > 0 && (
                  <span className="co-sidebar-head__count">
                    {activeItems.length} פריטים
                  </span>
                )}
              </div>

              {/* Cart items — uses CartDrawer CSS classes for identical appearance */}
              <div className="co-sidebar-list-wrap">
                <div className="cart-drawer__list co-sidebar-list">
                  {productsInCart.length === 0 ? (
                    <p className="cart-drawer__empty">העגלה ריקה</p>
                  ) : (
                    productsInCart.map((element) => {
                      const img = cartLineImageSrc(element.product);
                      const sidebarInSale =
                        element.promoId &&
                        element.quantity * element.unitPrice !==
                          element.totalPrice;
                      const sidebarSavedStr = sidebarInSale
                        ? (
                            element.unitPrice * element.quantity -
                            element.totalPrice
                          ).toFixed(2)
                        : null;
                      const sidebarDealNote = [
                        element.promoId ? "במבצע" : null,
                        sidebarInSale && sidebarSavedStr
                          ? `חסכת ${sidebarSavedStr} ₪`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <article
                          key={element.productId}
                          className="cart-drawer-line cart-drawer-line--narrow"
                        >
                          <div className="cart-drawer-line__layout">
                            <span className="cart-drawer-line__thumb-btn">
                              <span className="cart-drawer-line__thumb-wrap">
                                <img
                                  loading="lazy"
                                  src={img}
                                  alt=""
                                  className="cart-drawer-line__thumb-img"
                                />
                              </span>
                            </span>
                            <div className="cart-drawer-line__content">
                              <div className="cart-drawer-line__top">
                                <span className="cart-drawer-line__title-block">
                                  <span className="cart-drawer-line__name">
                                    {element.product?.name}
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  className="cart-drawer-line__dismiss-header"
                                  aria-label="הסר מהעגלה"
                                  onClick={() => handleDelete(element)}
                                >
                                  <X size={14} strokeWidth={2} aria-hidden />
                                </button>
                              </div>
                              <div className="cart-drawer-line__bottom">
                                <span className="cart-drawer-line__details-hit">
                                  <div className="cart-drawer-line__details-zone">
                                    {sidebarDealNote ? (
                                      <div className="cart-drawer-line__deal-note">
                                        {sidebarDealNote}
                                      </div>
                                    ) : null}
                                    <div className="cart-drawer-line__details-lines">
                                      <CartLineMeta product={element.product} />
                                    </div>
                                  </div>
                                </span>
                                <div
                                  className="cart-drawer-line__qty-zone"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                >
                                  <div className="cart-drawer-line__qty">
                                    <ProductAddToCart
                                      element={element}
                                      haveInfo={false}
                                    />
                                  </div>
                                </div>
                                <span className="cart-drawer-line__price-hit">
                                  <span className="cart-drawer-line__price-block">
                                    <span className="cart-drawer-line__price-num">
                                      {element.totalPrice.toFixed(2)}
                                    </span>
                                    <span className="cart-drawer-line__price-sym">
                                      {" "}
                                      ₪
                                    </span>
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Price summary — uses CartDrawer's summary CSS classes */}
              <div className="cart-drawer__summary co-sidebar-summary">
                <div className="cart-drawer__summary-row">
                  <span>סיכום מוצרים</span>
                  <span>₪{allSum.toFixed(2)}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="cart-drawer__summary-row">
                    <span>הנחת קופון</span>
                    <span>-₪{Number(couponDiscount).toFixed(2)}</span>
                  </div>
                )}
                <div className="cart-drawer__summary-row cart-drawer__summary-row--grand">
                  <span>סה&quot;כ הזמנה</span>
                  <span>₪{paymentOrder.toFixed(2)}</span>
                </div>
              </div>

              {/* Security note */}
              <div className="co-secure-note">
                <LockIcon />
                <span>תשלום מאובטח ומוצפן</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Payment popup ── */}
      {paymentStatus === "loading" || paymentStatus === "error" ? (
        <PaymentStatusScreen
          status={paymentStatus}
          onRetry={() => {
            setPaymentStatus(null);
            setToPayPopup(true);
          }}
          onHome={() => navigate("/")}
        />
      ) : null}

      {toPayPopup ? (
        <PayPopup
          SumPay={paymentOrder}
          tranmode={true}
          deferredDeliveryFee={displayShipping > 0 ? displayShipping : null}
          closePayPopup={(_succ, cardId, numPaymentSelect, responseData) =>
            splitPaymentsPay(_succ, cardId, numPaymentSelect, responseData)
          }
          coupons={appliedCouponCode ? [appliedCouponCode] : []}
          comment={comment}
        />
      ) : null}
    </div>
  );
};

export default ShopCart;
